
import {
  Component, Prop, Mixins,
} from 'vue-property-decorator';
import {
  Connection,
  PermissionCode,
  AssignmentAssignee,
  AssignmentEntry,
} from '@/types';
import ConnectionAssignmentsDialog from '@/components/ConnectionAssignmentsDialog.vue';
import AssignmentEntryItem from '@/components/AssignmentEntryItem.vue';
import { currentUserHasPermission, getAssignmentLabel } from '@/helpers';
import UtilsMixin from '@/mixins/UtilsMixin.vue';

@Component({
  components: {
    ConnectionAssignmentsDialog,
    AssignmentEntryItem,
  },
})
export default class ConnectionAssignments extends Mixins(UtilsMixin) {
  @Prop(Object) readonly connection!: Connection | null;

  loadingType: string | null = null
  editDialog = false
  editType = 'practice'

  mounted() {
    // Carica la configurazione degli assignment types se non è già presente
    if (this.$store.getters.getAssignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }
  }

  hasPermission(codename: PermissionCode): boolean { return currentUserHasPermission(codename); }

  get assignmentTypes() {
    return this.$store.getters.getAssignmentTypes;
  }

  getAssignmentTypeConfig(type: string) {
    return this.assignmentTypes.find((t: any) => t.id === type);
  }

  parseAssignees(raw: AssignmentAssignee[] | string | null | undefined): AssignmentAssignee[] {
    if (!raw) return [];
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return []; }
    }
    return raw;
  }

  buildEntry(
    type: string,
    config: any,
    dateTooltip: string,
    permissionShow: string,
    permissionEdit: string,
    assigneesRaw: AssignmentAssignee[] | string | null | undefined,
    flatAssigneeId: number | null,
    flatAssigneeName: string | null,
    flatAssigned: string | null,
    flatAssignedDate: Date | null,
  ): AssignmentEntry {
    const isMulti = config.multipleAssignees === true;
    const assignees = isMulti ? this.parseAssignees(assigneesRaw) : [];

    return {
      type,
      label: getAssignmentLabel(config, 'elettrica-figlia'),
      icon: config.icon || 'mdi-account',
      dateTooltip,
      permissionShow,
      permissionEdit,
      multipleAssignees: isMulti,
      assignees,
      assigneeId: flatAssigneeId,
      assigneeName: flatAssigneeName,
      assigned: isMulti ? this.aggregateStatus(assignees, flatAssigned) : flatAssigned,
      assignedDate: isMulti ? this.latestDate(assignees) : flatAssignedDate,
    };
  }

  aggregateStatus(assignees: AssignmentAssignee[], flatAssigned: string | null): string {
    if (flatAssigned === 'na') return 'na';
    if (assignees.length === 0) return 'todo';
    if (assignees.every((a) => a.status === 'done')) return 'done';
    return 'todo';
  }

  latestDate(assignees: AssignmentAssignee[]): Date | null {
    const dates = assignees
      .map((a) => a.assignedDate)
      .filter(Boolean)
      .map((d) => new Date(d as string | Date));
    if (dates.length === 0) return null;
    return dates.reduce((max, d) => (d > max ? d : max));
  }

  // Per i tipi multi-assegnatario: una entry "singola" per ciascun
  // assegnatario (più righe), così la cella mostra un AssignmentEntryItem per
  // installatore — coerente con OperationPmInstaller. Casi vuoto/NA gestiti.
  buildMultiEntries(
    type: string,
    config: any,
    dateTooltip: string,
    permissionShow: string,
    permissionEdit: string,
    assigneesRaw: AssignmentAssignee[] | string | null | undefined,
    flatAssigned: string | null,
  ): AssignmentEntry[] {
    const base = {
      type,
      label: getAssignmentLabel(config, 'elettrica-figlia'),
      icon: config.icon || 'mdi-account',
      dateTooltip,
      permissionShow,
      permissionEdit,
      multipleAssignees: false,
      assignees: [] as AssignmentAssignee[],
    };

    // Stato "Non applicabile" a livello di incarico
    if (flatAssigned === 'na') {
      return [{
        ...base, assigneeId: null, assigneeName: null, assigned: 'na', assignedDate: null,
      }];
    }

    const assignees = this.parseAssignees(assigneesRaw);
    // Nessun assegnatario: voce "Da assegnare" cliccabile per aggiungerne
    if (assignees.length === 0) {
      return [{
        ...base, assigneeId: null, assigneeName: null, assigned: 'todo', assignedDate: null,
      }];
    }

    return assignees.map((a) => ({
      ...base,
      assigneeId: a.assigneeId,
      assigneeName: a.assigneeName,
      assigned: a.status,
      assignedDate: a.assignedDate ? new Date(a.assignedDate) : null,
    }));
  }

  get entries(): AssignmentEntry[] {
    const result: AssignmentEntry[] = [];

    // Ordine colonna Incarichi: installatore in cima, poi project, practice,
    // enea, dogane.
    const installerConfig = this.getAssignmentTypeConfig('installer');
    if (installerConfig && this.hasPermission('installerAssignment.show')) {
      // Installatore: multi-assegnatario → una riga per assegnatario
      result.push(...this.buildMultiEntries(
        'installer',
        installerConfig,
        'Data assegnazione installatore',
        'installerAssignment.show',
        'installerAssignment.edit',
        this.connection?.installerAssignees,
        this.connection?.installerAssigned ?? null,
      ));
    }

    const projectConfig = this.getAssignmentTypeConfig('project');
    if (projectConfig && this.hasPermission('projectAssignment.show')) {
      result.push(this.buildEntry(
        'project',
        projectConfig,
        'Data assegnazione progetto',
        'projectAssignment.show',
        'projectAssignment.edit',
        this.connection?.projectAssignees,
        this.connection?.projectAssigneeId ?? null,
        this.connection?.projectAssigneeName ?? null,
        this.connection?.projectAssigned ?? null,
        this.connection?.projectAssignedDate ?? null,
      ));
    }

    const practiceConfig = this.getAssignmentTypeConfig('practice');
    if (practiceConfig && this.hasPermission('practiceAssignment.show')) {
      result.push(this.buildEntry(
        'practice',
        practiceConfig,
        'Data assegnazione pratiche',
        'practiceAssignment.show',
        'practiceAssignment.edit',
        this.connection?.practiceAssignees,
        this.connection?.practiceAssigneeId ?? null,
        this.connection?.practiceAssigneeName ?? null,
        this.connection?.practiceAssigned ?? null,
        this.connection?.practiceAssignedDate ?? null,
      ));
    }

    const eneaConfig = this.getAssignmentTypeConfig('enea');
    if (eneaConfig && this.hasPermission('eneaAssignment.show')) {
      result.push(this.buildEntry(
        'enea',
        eneaConfig,
        'Data assegnazione ENEA/GSE/Altro',
        'eneaAssignment.show',
        'eneaAssignment.edit',
        this.connection?.eneaAssignees,
        this.connection?.eneaAssigneeId ?? null,
        this.connection?.eneaAssigneeName ?? null,
        this.connection?.eneaAssigned ?? null,
        this.connection?.eneaAssignedDate ?? null,
      ));
    }

    const doganeConfig = this.getAssignmentTypeConfig('dogane');
    if (doganeConfig && this.hasPermission('doganeAssignment.show')) {
      result.push(this.buildEntry(
        'dogane',
        doganeConfig,
        'Data assegnazione Dogane',
        'doganeAssignment.show',
        'doganeAssignment.edit',
        this.connection?.doganeAssignees,
        this.connection?.doganeAssigneeId ?? null,
        this.connection?.doganeAssigneeName ?? null,
        this.connection?.doganeAssigned ?? null,
        this.connection?.doganeAssignedDate ?? null,
      ));
    }

    return result;
  }

  async onEdit(type: string) {
    const entry = this.entries.find((e) => e.type === type);
    if (!entry) return;

    if (this.loadingType || !this.hasPermission(entry.permissionEdit as PermissionCode)) return;

    if (this.connection) {
      this.loadingType = type;
      await this.$store.dispatch('selectConnection', { id: this.connection.id });
      this.editType = type;
      this.editDialog = true;
      this.loadingType = null;
    }
  }
}
